import { useMemo, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  Calendar,
  Clock,
  ArrowRight,
  Users,
  Settings,
  AlertCircle,
  Info,
  Shield,
  Eye,
  Edit,
  HardDrive,
  Trash2,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useCustomerStore } from '@/store/customerStore'
import { getDashboardSections } from '@/utils/rbac'
import { normalizeRole } from '@/security/accessContract'
import { format } from 'date-fns'
import InsightsPanel from '@/components/dashboard/InsightsPanel'
import { apiClient } from '@/services/http/client'

type SourceState = 'loading' | 'ready' | 'unavailable'
type CustomerSummary = { total: number; active: number; archived: number; deleted: number; pending_assessments: number; overdue_assessments: number }
type UserSummary = { total: number; active: number; inactive: number; deleted: number }
type DocumentSummary = { active_documents: number; deleted_documents: number; active_storage_bytes: number; recoverable_storage_bytes: number }
type DeletionSummary = { total: number; restorable: number; purge_eligible: number; on_hold: number; requested: number; failed: number }

const formatBytes = (value: number) => {
  if (value < 1024) return `${value} B`
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`
  if (value < 1024 ** 3) return `${(value / 1024 ** 2).toFixed(1)} MB`
  return `${(value / 1024 ** 3).toFixed(1)} GB`
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const { user, hasPermission } = useAuth()
  const customers = useCustomerStore((s) => s.customers)
  const loadCustomers = useCustomerStore((s) => s.loadCustomers)
  const [customerSummary, setCustomerSummary] = useState<CustomerSummary | null>(null)
  const [userSummary, setUserSummary] = useState<UserSummary | null>(null)
  const [documentSummary, setDocumentSummary] = useState<DocumentSummary | null>(null)
  const [deletionSummary, setDeletionSummary] = useState<DeletionSummary | null>(null)
  const [sourceState, setSourceState] = useState<Record<string, SourceState>>({
    customers: 'loading', users: 'loading', documents: 'loading', deletions: 'loading',
  })
  const effectiveRole = normalizeRole(user?.role ?? '')
  const isFirmAdministrator = effectiveRole === 'firm_owner' || effectiveRole === 'firm_admin'

  // Load real data on mount (mock or live, per API mode)
  useEffect(() => {
    loadCustomers()
  }, [loadCustomers])

  useEffect(() => {
    if (!user) return
    let active = true
    const loadSummaries = async () => {
      setSourceState({ customers: 'loading', users: 'loading', documents: 'loading', deletions: 'loading' })
      const results = await Promise.allSettled([
        apiClient.get<CustomerSummary>('/customers/dashboard-summary'),
        isFirmAdministrator ? apiClient.get<UserSummary>('/users/summary') : Promise.resolve(null),
        isFirmAdministrator ? apiClient.get<DocumentSummary>('/documents/summary') : Promise.resolve(null),
        isFirmAdministrator ? apiClient.get<DeletionSummary>('/deletions/summary') : Promise.resolve(null),
      ] as const)
      if (!active) return
      const state: Record<string, SourceState> = {
        customers: results[0]?.status === 'fulfilled' ? 'ready' : 'unavailable',
        users: isFirmAdministrator && results[1]?.status === 'fulfilled' ? 'ready' : 'unavailable',
        documents: isFirmAdministrator && results[2]?.status === 'fulfilled' ? 'ready' : 'unavailable',
        deletions: isFirmAdministrator && results[3]?.status === 'fulfilled' ? 'ready' : 'unavailable',
      }
      if (results[0]?.status === 'fulfilled') setCustomerSummary(results[0].value.data)
      if (results[1].status === 'fulfilled' && results[1].value) setUserSummary(results[1].value.data)
      if (results[2].status === 'fulfilled' && results[2].value) setDocumentSummary(results[2].value.data)
      if (results[3].status === 'fulfilled' && results[3].value) setDeletionSummary(results[3].value.data)
      setSourceState(state)
    }
    void loadSummaries()
    return () => { active = false }
  }, [isFirmAdministrator, user])

  // The list remains useful for attention cards; headline counts come from the
  // authoritative aggregate API so pagination cannot under-count them.
  const pendingCustomers = useMemo(
    () => customers.filter((c) => c.pendingAssessments > 0),
    [customers]
  )
  const metrics = {
    totalCustomers: customerSummary?.total ?? 0,
    activeCustomers: customerSummary?.active ?? 0,
    archivedCustomers: customerSummary?.archived ?? 0,
    pendingAssessments: customerSummary?.pending_assessments ?? 0,
    overdueAssessments: customerSummary?.overdue_assessments ?? 0,
  }

  if (!user) {
    return <div>Not authenticated</div>
  }

  // Get dashboard sections visible for this role
  const dashboardSections = getDashboardSections(user.role)

  const isOwner = effectiveRole === 'firm_owner'
  const isAdmin = effectiveRole === 'firm_admin'
  const isPartner = effectiveRole === 'ca_partner'
  const isTechAssistant = effectiveRole === 'technical_assistant'
  const isResearchAssistant = effectiveRole === 'research_assistant'
  const isAssistant = isTechAssistant || isResearchAssistant || effectiveRole === 'staff'
  const isClient =
    effectiveRole === 'individual_client' ||
    effectiveRole === 'business_owner_client' ||
    effectiveRole === 'authorized_client' ||
    effectiveRole === 'client'

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="card card-elevated p-6 sm:p-8 bg-gradient-to-br from-primary-50 via-surface-light to-surface-light dark:from-primary-900/20 dark:via-surface-dark dark:to-surface-dark border-l-4 border-primary-500">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-text-primary tracking-tight">
              Welcome back, {user.firstName}!
            </h1>
            <p className="text-text-secondary">
              {isOwner && 'Your practice at a glance'}
              {isAdmin && 'Firm operations and administrative overview'}
              {isPartner && 'Your assigned filings and client portfolio review'}
              {isTechAssistant && 'Assigned tax preparation, documents, and filing support'}
              {isResearchAssistant && 'Legal research, advisory drafts, and case analysis'}
              {isClient && 'Your filing status and updates'}
            </p>
          </div>
          <span className="badge badge-primary gap-2 flex-shrink-0">
            <span className="w-2 h-2 bg-white rounded-full" />
            {isOwner && 'Firm Owner'}
            {isAdmin && 'Firm Admin'}
            {isPartner && 'CA Partner'}
            {isTechAssistant && 'Technical Assistant'}
            {isResearchAssistant && 'Research Assistant'}
            {isClient && 'Client'}
          </span>
        </div>
        <div className="text-sm text-text-tertiary pt-4">
          Last login: {user.lastLoginAt ? format(new Date(user.lastLoginAt), 'MMM d, yyyy h:mm a') : 'First login'}
        </div>
      </div>

      {/* Role-Specific Capabilities Info */}
      <div className="card card-elevated p-6 bg-gradient-to-r from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-900/10 border-l-4 border-primary-500">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-white dark:bg-surface-dark shadow-sm">
            <Shield className="w-6 h-6 text-primary-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-text-primary mb-2 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Your Access Level
            </h3>

            {isOwner && (
              <div className="space-y-2">
                <p className="text-sm text-text-secondary">
                  As <strong>Firm Owner</strong>, you have unrestricted access across the practice:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Edit className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span>Manage all client filings and tax returns</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span>Manage team members, roles, and assignments</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Settings className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span>Manage organization, security, and billing</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Eye className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span>View all customers, audit logs, and practice insights</span>
                  </div>
                </div>
              </div>
            )}

            {isAdmin && (
              <div className="space-y-2">
                <p className="text-sm text-text-secondary">
                  As <strong>Firm Administrator</strong>, you coordinate team operations:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span>Onboard team members and assign roles</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Settings className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span>Configure organization and security policies</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span>Monitor user sessions and audit logs</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span>Assign client files to CA Partners and assistants</span>
                  </div>
                </div>
              </div>
            )}

            {isPartner && (
              <div className="space-y-2">
                <p className="text-sm text-text-secondary">
                  As a <strong>CA Partner</strong>, you oversee client portfolios and sign off on filings:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span>Review and approve tax computations and ITR/GST drafts</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span>Statutory signing authority for e-filing submissions</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span>Assign customer files to Technical and Research Assistants</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span>Access Practice Governance and workload metrics</span>
                  </div>
                </div>
              </div>
            )}

            {isTechAssistant && (
              <div className="space-y-2">
                <p className="text-sm text-text-secondary">
                  As a <strong>Technical Assistant</strong>, you prepare filings for assigned customers:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <FileText className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <span>Verify Form 16, 26AS, AIS/TIS, and bank statements</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Edit className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <span>Draft ITR and GST returns and computation schedules</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Eye className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <span>View and work on assigned customer records</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <span>Submit completed drafts to CA Partner for sign-off</span>
                  </div>
                </div>
              </div>
            )}

            {isResearchAssistant && (
              <div className="space-y-2">
                <p className="text-sm text-text-secondary">
                  As a <strong>Research Assistant</strong>, you provide legal research and advisory support:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <FileText className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span>Research judicial precedents, case laws, and tribunal rulings</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Edit className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span>Prepare advisory memorandums and draft notice replies</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Eye className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span>Link legal citations to customer assessments</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Info className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span>View and analyze tax insights and recommendations</span>
                  </div>
                </div>
              </div>
            )}

            {isClient && (
              <div className="space-y-2">
                <p className="text-sm text-text-secondary">
                  As a <strong>Client</strong>, you can track your filings:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Eye className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span>View your filing details</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span>See upcoming deadlines</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <FileText className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span>Access your documents</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Info className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span>Read filing updates</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Key Metrics Section */}
      {dashboardSections.caseMetrics && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Customers Card */}
            <div className="card card-elevated p-6 space-y-3 hover:shadow-lg transition cursor-pointer"
                 onClick={() => navigate('/customers')}>
              <div className="flex items-center justify-between">
                <div className="text-text-secondary text-sm font-medium">Total Customers</div>
                <span className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                  <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </span>
              </div>
              <div className="space-y-1">
                <div className="text-3xl font-bold text-text-primary">
                  {sourceState.customers === 'loading' ? '…' : sourceState.customers === 'ready' ? metrics.totalCustomers : '—'}
                </div>
                <div className="text-xs text-text-tertiary">
                  {sourceState.customers === 'ready' ? `${metrics.activeCustomers} active · ${metrics.archivedCustomers} archived` : 'Customer metrics unavailable'}
                </div>
              </div>
            </div>

            {/* Pending Assessments Card */}
            <div className="card card-elevated p-6 space-y-3 hover:shadow-lg transition">
              <div className="flex items-center justify-between">
                <div className="text-text-secondary text-sm font-medium">Pending Assessments</div>
                <span className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                  <Calendar className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </span>
              </div>
              <div className="space-y-1">
                <div className="text-3xl font-bold text-text-primary">
                  {sourceState.customers === 'loading' ? '…' : sourceState.customers === 'ready' ? metrics.pendingAssessments : '—'}
                </div>
                <div className="text-xs text-text-tertiary">
                  Customers with pending filings
                </div>
              </div>
            </div>

            {/* Overdue Assessments Card */}
            {(isOwner || isPartner) && (
              <div className="card card-elevated p-6 space-y-3 hover:shadow-lg transition">
                <div className="flex items-center justify-between">
                  <div className="text-text-secondary text-sm font-medium">Overdue</div>
                  <span className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                    <Clock className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="text-3xl font-bold text-text-primary">
                    {sourceState.customers === 'loading' ? '…' : sourceState.customers === 'ready' ? metrics.overdueAssessments : '—'}
                  </div>
                  <div className="text-xs text-text-tertiary">
                    Customers with overdue filings
                  </div>
                </div>
              </div>
            )}

            {/* Management Card - Owner only */}
            {isFirmAdministrator && (
              <div className="card card-elevated p-6 space-y-3 hover:shadow-lg transition cursor-pointer"
                   onClick={() => navigate('/users')}>
                <div className="flex items-center justify-between">
                  <div className="text-text-secondary text-sm font-medium">Team Members</div>
                  <span className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                    <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="text-3xl font-bold text-text-primary">
                    {sourceState.users === 'loading' ? '…' : sourceState.users === 'ready' ? userSummary?.active : '—'}
                  </div>
                  <div className="text-xs text-text-tertiary">
                    {sourceState.users === 'ready' ? `${userSummary?.inactive ?? 0} inactive · ${userSummary?.deleted ?? 0} deleted` : 'User metrics unavailable'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isFirmAdministrator && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Governance &amp; storage</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="card card-elevated p-5 text-left hover:shadow-lg transition" onClick={() => navigate('/recycle-bin')}>
              <div className="flex items-center justify-between"><span className="text-sm font-medium text-text-secondary">Recycle bin</span><Trash2 className="h-5 w-5 text-orange-600" /></div>
              <div className="mt-3 text-2xl font-bold text-text-primary">{sourceState.deletions === 'loading' ? '…' : sourceState.deletions === 'ready' ? deletionSummary?.total : '—'}</div>
              <div className="mt-1 text-xs text-text-tertiary">{sourceState.deletions === 'ready' ? `${deletionSummary?.restorable ?? 0} restorable · ${deletionSummary?.on_hold ?? 0} on hold · ${deletionSummary?.failed ?? 0} failed` : 'Deletion metrics unavailable'}</div>
            </button>
            <button className="card card-elevated p-5 text-left hover:shadow-lg transition" onClick={() => navigate('/documents')}>
              <div className="flex items-center justify-between"><span className="text-sm font-medium text-text-secondary">Document storage</span><HardDrive className="h-5 w-5 text-blue-600" /></div>
              <div className="mt-3 text-2xl font-bold text-text-primary">{sourceState.documents === 'loading' ? '…' : sourceState.documents === 'ready' ? formatBytes(documentSummary?.active_storage_bytes ?? 0) : '—'}</div>
              <div className="mt-1 text-xs text-text-tertiary">{sourceState.documents === 'ready' ? `${documentSummary?.active_documents ?? 0} active · ${formatBytes(documentSummary?.recoverable_storage_bytes ?? 0)} retained for recovery` : 'Document metrics unavailable'}</div>
            </button>
            <button className="card card-elevated p-5 text-left hover:shadow-lg transition" onClick={() => navigate('/recycle-bin')}>
              <div className="flex items-center justify-between"><span className="text-sm font-medium text-text-secondary">Purge readiness</span><Shield className="h-5 w-5 text-red-600" /></div>
              <div className="mt-3 text-2xl font-bold text-text-primary">{sourceState.deletions === 'loading' ? '…' : sourceState.deletions === 'ready' ? deletionSummary?.purge_eligible : '—'}</div>
              <div className="mt-1 text-xs text-text-tertiary">{sourceState.deletions === 'ready' ? `${deletionSummary?.requested ?? 0} cleanup requests awaiting platform review` : 'Governance source unavailable'}</div>
            </button>
          </div>
        </div>
      )}

      {/* Quick Actions Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-text-primary">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          {hasPermission('txh.client.create') && (
            <button
              onClick={() => navigate('/customers?new=true')}
              className="btn btn-primary flex items-center gap-2"
            >
              <Users className="h-4 w-4" />
              Add New Customer
            </button>
          )}
          {hasPermission('txh.client.read') && (
            <button
              onClick={() => navigate('/customers')}
              className="btn btn-outline flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              View All Customers
            </button>
          )}
        </div>
      </div>

      {/* Recent Customers with Pending Assessments */}
      {dashboardSections.alerts && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Customers Needing Attention</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pendingCustomers.slice(0, 4).map((customer) => (
              <div
                key={customer.id}
                onClick={() => navigate(`/customers/${customer.id}`)}
                className="card card-elevated p-4 space-y-3 cursor-pointer hover:shadow-lg transition group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-text-primary line-clamp-1">
                      {customer.fullName}
                    </p>
                    <p className="text-xs text-text-tertiary">
                      PAN: {customer.pan}
                    </p>
                  </div>
                  <Users className="w-4 h-4 text-primary-600 flex-shrink-0" />
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-surface-overlay">
                  <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full dark:bg-orange-900 dark:text-orange-200">
                    {customer.pendingAssessments} pending
                  </span>
                  <ArrowRight className="w-4 h-4 text-text-tertiary opacity-0 group-hover:opacity-100 transition" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights / Recommendations */}
      {!isClient && hasPermission('txh.insight.read') && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Insights</h2>
          <InsightsPanel />
        </div>
      )}

      {/* Admin Section - Owner only */}
      {isOwner && dashboardSections.settings && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Administration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/users')}
              className="card card-elevated p-4 hover:shadow-lg transition flex items-center justify-between group"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary-600" />
                <div className="text-left">
                  <p className="font-medium text-text-primary">Team Management</p>
                  <p className="text-xs text-text-tertiary">Manage users and permissions</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-text-tertiary opacity-0 group-hover:opacity-100 transition" />
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="card card-elevated p-4 hover:shadow-lg transition flex items-center justify-between group"
            >
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-primary-600" />
                <div className="text-left">
                  <p className="font-medium text-text-primary">Tenant Settings</p>
                  <p className="text-xs text-text-tertiary">Configure your workspace</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-text-tertiary opacity-0 group-hover:opacity-100 transition" />
            </button>
          </div>
        </div>
      )}

      {/* Empty State - For non-owner users with no assigned customers */}
      {sourceState.customers === 'ready' && metrics.totalCustomers === 0 && !isOwner && (
        <div className="card card-elevated p-12 text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-text-tertiary mx-auto" />
          <div>
            <h3 className="font-semibold text-text-primary mb-2">No Customers Assigned</h3>
            <p className="text-text-secondary text-sm">
              {isPartner && 'You don\'t have any assigned customers yet. Your supervisor will assign customers to you soon.'}
              {isAssistant && 'You don\'t have any customers assigned yet. Your supervisor will assign customers to you soon.'}
              {isClient && 'No customer data available.'}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
